import { saveWorkspaceInfo } from "../api/workspace";
import store from "../redux/store";
import { postSubmitStatus } from "../utils/utilPostMessage";
import { getCalendarManagementSaveObject } from "./calendar-management/calendar-management-logic";
import { getCompanyDirectorySaveObject } from "./company-directory/company-directory-logic";
import { getKnowledgeBaseSaveObject } from "./knowledge-base/knowledge-base-logic";
import { getWorkspaceSaveObject } from "./your-receptionist/your-receptionist-logic";

export const scrollToFirstErrorElement = (elementId) => {
  const element = document.getElementById(elementId);
  if (element) {
    setTimeout(() => {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }
};

export const compareObjects = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export const getAccountSaveObject = () => {
  const account = store.getState().info?.account;

  if (!account) {
    return {};
  }

  return {
    phoneNumber: account.phoneNumber,
    phoneProvider: account.phoneProvider,
    phoneNumberType: account.phoneNumberType
      ? account.phoneNumberType
      : account.phoneNumber && account.phoneNumber.length > 0
      ? "use-pre-allocated"
      : "none",
  };
};

export const getSystemFeatureFlag = (featureKey, defaultValue = null) => {
  const { systemFeatureFlags } = store.getState().info;

  if (!systemFeatureFlags) {
    return defaultValue;
  }

  return systemFeatureFlags[featureKey];
};

export const getOrganizationFeatureFlag = (businessKey, featureKey, defaultValue = null) => {
  const { featureFlags } = store.getState().info?.organization;
  if (!featureFlags) {
    return defaultValue;
  }

  const businessObject = featureFlags[businessKey];
  if (!businessObject) {
    return defaultValue;
  }

  if (featureKey === null) {
    return businessObject;
  }

  return businessObject[featureKey];
};

export const getAccountFeatureFlags = (businessKey, featureKey) => {
  const { featureFlags } = store.getState().info?.account;

  if (!featureFlags) {
    return null;
  }

  const businessObject = featureFlags[businessKey];
  if (!businessObject) {
    return null;
  }

  if (featureKey === null) {
    return businessObject;
  }

  return businessObject[featureKey];
};

export const getFeatureFlag = (bussinessName, flagName = null, defaultValue = null) => {
  var feature = getAccountFeatureFlags(bussinessName, flagName);
  if (feature) {
    return feature;
  }

  feature = getOrganizationFeatureFlag(bussinessName, flagName);
  if (feature) {
    return feature;
  }

  feature = getSystemFeatureFlag(bussinessName, defaultValue);
  if (feature) {
    return feature;
  }

  return defaultValue;
};

export const createWorkspaceObject = async (organizationId, accountId, workspaceId) => {
  var saveObject = {};

  /**
   * {
  "channelId": "",
  "organization": {"id": "", "name": "", "featureFlags": {}},
  "account": {"id": "", "name": ""},
  "workspace": {"id": "", "name": ""},
  "agentName": "",
  "agentLanguage": "",
  "greeting": "",
  "voices": [],
  "voice": "",
  "isNew": false,
  "companyDirectory": {"contacts": [], "destinations": []},
  "calendarManagement": {
    "calendarInfo": {
      "provider"      : "google",
      "aliasName"     : "",
      "id"            : "",
      "credentialsKey": ""
    },
    "schedulingWindows": [],
    "isSameAsOpeningHours": false,
    "appointmentTypes": []
  },
  "knowledgeBase": [
    {"type": "plain-text", "name": "", "content": ""},
    {
      "type": "opening-hours",
      "name": "Opening Hours",
      "openingHours": [
        {"day": "Monday",  "open": "9:00", "close": "17:00"},
        {"day": "Tuesday", "open": "9:00", "close": "17:00"}
      ]
    }
  ]
}

   */

  const info = store.getState().info;

  saveObject.channelId = info.updatedChannelId;
  saveObject.organization = {
    id: info.organization.id,
    name: info.organization.name,
  };

  saveObject.account = {
    id: info.account.id,
    name: info.account.name,
  };

  saveObject.workspace = {
    id: info.workspace.id,
    name: info.workspace.name,
  };

  saveObject.agentName = info.agentName;
  saveObject.agentId = info.agentId;
  saveObject.agentLanguage = info.agentLanguage;

  saveObject.greeting = info.updatedGreeting;
  saveObject.voice = info.updatedVoice;
  saveObject.isNew = info.isNew;

  saveObject.companyDirectory = getCompanyDirectorySaveObject();
  saveObject.calendarManagement = await getCalendarManagementSaveObject();
  saveObject.knowledgeBase = getKnowledgeBaseSaveObject();

  return saveObject;
};

export const validateField = (
  required,
  title,
  value,
  minCharacters,
  language = "en-US",
  validation = () => true,
  failedValidationMessage = ""
) => {
  if ((!value || value.length === 0) && required) {
    return `${title} is required`;
  }

  if (!value || value.length === 0) {
    return "";
  }

  if (minCharacters > 0 && value.length < minCharacters) {
    return `${title} must be at least ${minCharacters} characters`;
  }

  const isValid = validation(value, language);
  if (typeof isValid === "boolean" && !isValid) {
    return failedValidationMessage || `${title} is invalid`;
  }

  if (typeof isValid === "string") {
    return isValid;
  }

  return "";
};

export const getSaveSessionId = async () => {
  const organizationId = store.getState().info.organizationId;
  const accountId = store.getState().info.accountId;
  const workspaceId = store.getState().info.workspaceId;

  try {
    const saveRequest = await createWorkspaceObject(organizationId, accountId, workspaceId);

    const response = await fetch(`${window.appConfig.API_BASE_URL}/sse-workspace-info`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(saveRequest),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    if (data.sessionId) {
      return data.sessionId;
    }

    return null;
  } catch (error) {
    return null;
  }
};
